import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import type {
  DailyRouteActivity,
  DailyRouteStop,
  SafetyEvent,
} from '@apis/reports';
import { APP_DEFAULT_TZ, formatDuration, formatTimeRange } from '@utils/dateUtils';
import { ActivitiesSection } from '../../shared/ActivitiesSection';
import { EquipmentSection } from '../../shared/EquipmentSection';
import { PersonnelSection } from '../../shared/PersonnelSection';
import { SafetyEventsSection } from './SafetyEventsSection';
import styles from './StopDetail.module.css';

interface QueryShape<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

interface IStopDetailProps {
  stop: DailyRouteStop;
  activities: QueryShape<DailyRouteActivity[]>;
  safetyEvents: QueryShape<SafetyEvent[]>;
  /** True when the stop covers the incident instant. Renders a red badge
   * next to the "LOCATION N" eyebrow, matching trusstor's marker. */
  isIncidentStop: boolean;
}

/**
 * Middle pane of the Incident Report. Same header shape as Daily Route's
 * `StopDetail` (eyebrow / floor row / sub row) with an Incident-specific
 * badge on the eyebrow line, and a SAFETY EVENTS section inserted between
 * Personnel and Activities.
 */
export function StopDetail({ stop, activities, safetyEvents, isIncidentStop }: IStopDetailProps) {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const timeText = formatTimeRange(stop.arrivalIso, stop.departureIso, tz);
  return (
    <div className={styles.detail}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>
          {isIncidentStop && (
            <span
              className={styles.incidentBadge}
              aria-label={t('reports.incidentReport.atIncidentBadge')}
              role="img"
            >
              <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="10" cy="10" r="10" fill="currentColor" />
                <path
                  d="M10 5v6m0 1.5v1.5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          )}
          {t('reports.incidentReport.location', { n: stop.stopIndex })}
        </span>
        <div className={styles.floorRow}>
          <span className={styles.floor}>{stop.floorNick}</span>
          <span className={styles.duration}>{formatDuration(stop.durationMinutes)}</span>
        </div>
        <div className={styles.subRow}>
          <span className={styles.area}>{stop.areaNick}</span>
          <span className={styles.time}>{timeText}</span>
        </div>
      </header>

      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>
          {t('reports.incidentReport.section.personnel')}
        </h3>
        <PersonnelSection
          personnel={stop.personnel}
          fallbackTimeText={timeText}
          emptyKey="reports.incidentReport.empty.personnel"
        />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>
          {t('reports.incidentReport.section.safetyEvents')}
        </h3>
        <SafetyEventsSection events={safetyEvents.data ?? []} />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>
          {t('reports.incidentReport.section.activities')}
        </h3>
        <ActivitiesSection
          activities={activities.data ?? []}
          isLoading={activities.isLoading}
          isError={activities.isError}
          onRetry={activities.refetch}
          i18nNamespace="reports.incidentReport"
        />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>
          {t('reports.incidentReport.section.equipment')}
        </h3>
        <EquipmentSection
          equipment={stop.equipment}
          emptyKey="reports.incidentReport.empty.equipment"
        />
      </section>
    </div>
  );
}
